import { BaseDto } from '../../types';
import { ValidationFailure, ValidationResult } from 'fluent-ts-validator';
import i18n from '../../../i18n';
import { repository } from 'redux-scaffolding-ts';
import { DataStore } from '../../dataStore';
import { FormStore } from '../../formStore';
import { container } from 'inversify.config';
import HttpService from 'services/http-service';
import { QuestionDto, CreateQuestionDto, ChangeQuestionDto, QuestionDtoValidator } from './tna-questionBank-store';
import { IwsSkillDto } from '../skills/iws-skills-store';
import { isNullOrWhiteSpaces } from 'utils/useful-functions';
import ExtendedAbstractValidator from 'utils/extended-abstract-validator';

export interface IwsQuestionBankDto extends BaseDto {
  id: string; //Guid
  friendlyId: string;
  question: QuestionDto;
  iwsSkillId: string;
  iwsSkill: IwsSkillDto;
  questionTranslations: QuestionDto[];
  locationId: string;
  type: string;
}

export interface CreateIwsQuestionBankDto {
  question: CreateQuestionDto;
  iwsSkillId: string;
  questionTranslations: CreateQuestionDto[];
  locationRequired: boolean;
  locationId: string;
}

export interface ChangeIwsQuestionBankDto {
  id: string; //Guid
  friendlyId: string;
  question: ChangeQuestionDto;
  iwsSkillId: string;
  questionTranslations: ChangeQuestionDto[];
  locationRequired: boolean;
  locationId: string;
}

export class CreateIwsQuestionBankValidator extends ExtendedAbstractValidator<CreateIwsQuestionBankDto> {
  constructor(onErrors?: (...failures: ValidationFailure[]) => void) {
    super(onErrors);

    this.validateIf(x => x)
      .isDefined()
      .isNotNull()
      .withFailureMessage(i18n.t('No data provided'));

    this.validateIfString(o => o.locationId)
      .isDefined()
      .isNotNull()
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(i18n.t('Location is required'))
      .when(o => o.locationRequired);

    this.validateIf(o => o.question)
      .isDefined()
      .isNotNull()
      .when(x => x != null)
      .withFailureMessage(i18n.t('Question is required'));

    this.validateIf(x => x.question)
      .fulfills(x => new QuestionDtoValidator(false, null, null, null, this.addErrors).extendValidate(x).isValid())
      .when(x => x != null && x.question != null)
      .withFailureMessage(i18n.t('Question is wrong'));

    this.validateIf(x => x)
      .fulfills(x =>
        x.questionTranslations.all((t, i) =>
          new QuestionDtoValidator(true, x.question.languageId, x.questionTranslations, i, this.addErrors).extendValidate(t).isValid()
        )
      )
      .when(
        x => x != null && x.question != null && !isNullOrWhiteSpaces(x.question.languageId) && (x.questionTranslations || []).length !== 0
      )
      .withFailureMessage(i18n.t('At least one translation is wrong'));
  }
}

export class ChangeIwsQuestionBankValidator extends ExtendedAbstractValidator<ChangeIwsQuestionBankDto> {
  constructor(onErrors?: (...failures: ValidationFailure[]) => void) {
    super(onErrors);

    this.validateIf(x => x)
      .isDefined()
      .isNotNull()
      .withFailureMessage(i18n.t('No data provided'));

    this.validateIfString(o => o.id)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(i18n.t('Question Bank Id is required'));

    this.validateIfString(o => o.locationId)
      .isDefined()
      .isNotNull()
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(i18n.t('Location is required'))
      .when(o => o.locationRequired);

    this.validateIf(o => o.question)
      .isDefined()
      .isNotNull()
      .when(x => x != null)
      .withFailureMessage(i18n.t('Question is required'));

    this.validateIf(x => x.question)
      .fulfills(x => new QuestionDtoValidator(false, null, null, null, this.addErrors).extendValidate(x).isValid())
      .when(x => x != null && x.question != null)
      .withFailureMessage(i18n.t('Question is wrong'));

    this.validateIf(x => x)
      .fulfills(x =>
        x.questionTranslations.all((t, i) =>
          new QuestionDtoValidator(true, x.question.languageId, x.questionTranslations, i, this.addErrors).extendValidate(t).isValid()
        )
      )
      .when(
        x => x != null && x.question != null && !isNullOrWhiteSpaces(x.question.languageId) && (x.questionTranslations || []).length !== 0
      )
      .withFailureMessage(i18n.t('At least one translation is wrong'));
  }
}

@repository('@@IWSQUESTIONBANKS', 'iwsquestionbanks.summary')
export class IwsQuestionBanksStore extends DataStore<IwsQuestionBankDto> {
  baseUrl = 'skills/v1';
  createPath = 'new-iws-question';
  retrievePath = 'get-iws-questions';
  updatePath = 'update-iws-question';
  deletePath = 'delete-iws-question';
  retrieveOnePath = 'get-iws-question';
  retrieveCountPath = 'get-used-iws-question-count';

  protected validate(item: IwsQuestionBankDto) {
    return new ValidationResult();
  }

  constructor() {
    super('IWSQUESTIONBANK', {
      isBusy: false,
      items: [],
      count: 0,
      result: undefined,
      discard: item => {}
    });
  }

  public async getQuestionBankById(id: string): Promise<IwsQuestionBankDto> {
    const httpService = container.get(HttpService);
    const result = await this.dispatchAsync(
      this.retrieveOnePath,
      httpService.get<IwsQuestionBankDto>(`${this.baseUrl}/${this.retrieveOnePath}/${id}`)
    );
    return result.data;
  }

  public async getUsedTnaQuestionCountById(id: string): Promise<number> {
    const httpService = container.get(HttpService);
    const result = await httpService.get<number>(`${this.baseUrl}/${this.retrieveCountPath}/${id}`);

    return result.data;
  }
}

@repository('@@IWSQUESTIONBANKS', 'iwsquestionbanks.new')
export class NewIwsQuestionBankStore extends FormStore<CreateIwsQuestionBankDto> {
  baseUrl = 'skills/v1';
  createPath = 'new-iws-question';
  retrievePath = 'get-iws-questions';
  updatePath = 'update-iws-question';

  protected validate(item: CreateIwsQuestionBankDto) {
    return new CreateIwsQuestionBankValidator().extendValidate(item);
  }

  constructor() {
    super('NEW_IWSQUESTIONBANKS', {
      isBusy: false,
      status: 'New',
      item: undefined,
      result: undefined
    });
  }
}

@repository('@@IWSQUESTIONBANKS', 'iwsquestionbanks.change')
export class ChangeIwsQuestionBankStore extends FormStore<ChangeIwsQuestionBankDto> {
  baseUrl = 'skills/v1';
  createPath = 'new-iws-question';
  retrievePath = 'get-iws-questions';
  updatePath = 'update-iws-question';

  protected validate(item: ChangeIwsQuestionBankDto) {
    return new ChangeIwsQuestionBankValidator().extendValidate(item);
  }

  constructor() {
    super('CHANGE_IWSQUESTIONBANKS', {
      isBusy: false,
      status: 'Modified',
      item: undefined,
      result: undefined
    });
  }
}
